import { createApi } from '@reduxjs/toolkit/query/react';
import { filteredFilter } from '../../utils/api';
import {properties} from "../../properties.js";
import { authBaseQuery } from '../../app/axiosBaseQuery';
import { PaginationResult, ListRequest } from '../../types/global.d';
import * as Entity from '../types/models/ContractorUser';
import {createNormalizationMiddleware} from "../../app/normy/normyRtkQuery.ts";

export const CONTRACTOR_USER_API_REDUCER_KEY = 'contractor-users';
export const CONTRACTOR_USER_PATH = 'contractor-users';

export const contractorUserApi = createApi({
    reducerPath: CONTRACTOR_USER_API_REDUCER_KEY,
    baseQuery: authBaseQuery(),
    keepUnusedDataFor: 10,
    refetchOnMountOrArgChange: 10,
    refetchOnFocus: true,
    tagTypes: ['ContractorUser', 'ContractorUsers'],
    endpoints: (builder) => ({
        getContractorUserList: builder.query<PaginationResult<Entity.ContractorUser>, ListRequest>({
            query: (request) => {
                const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request))
                return ({
                    url: `/${ CONTRACTOR_USER_PATH }?${new URLSearchParams(queryParams).toString()}`,
                    method: 'GET',
                })
            },
            providesTags: ['ContractorUsers'],
        }),
        getContractorUser: builder.query<Entity.ContractorUser, Entity.PrimaryContractorUser>({
            queryFn: async (id, { getState }, extraOptions, baseQuery) => {

                const cachedData = contractorUserNormalizer.getObjectById(id.id);

                if (cachedData) {
                    return { data: cachedData };
                } else {
                    const response = await baseQuery({
                        url: `/${ CONTRACTOR_USER_PATH }/${id.toPath()}`,
                        method: 'GET',
                    });

                    return response;
                }
            },
            providesTags: ['ContractorUser'],
        }),
        getContractorUserMultiple: builder.query<Entity.ContractorUser, Entity.PrimaryContractorUser[]>({
            query: (ids) => {
                return ({
                    url: `/${ CONTRACTOR_USER_PATH }/ids${Entity.PrimaryContractorUser.toQueryParams(ids)}`,
                    method: 'GET',
                })
            },
            providesTags: ['ContractorUsers'],
        }),
        getContractorUserPresentation: builder.query<PaginationResult<Entity.ContractorUser>, Entity.PresentationContractorUserRequest>({
            query: (request) => {
              const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request.list))
              return ({
                url: `/${ CONTRACTOR_USER_PATH }/presentation?${new URLSearchParams(queryParams).toString()}`,
                method: 'POST',
                data: request.filter,
              })
            },
            providesTags: ['ContractorUsers'],
        }),
        
        insertContractorUser: builder.mutation<Entity.ContractorUser, Entity.CreateContractorUser>({
            query: (toInsert) => {
                return ({
                    url: `/${ CONTRACTOR_USER_PATH }`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                dispatch(
                    contractorUserApi.util.upsertQueryData('getContractorUser', Entity.PrimaryContractorUser.fromId(mutationData.id), mutationData),
                );
            },
            invalidatesTags: ['ContractorUsers'],
        }),
        insertMultipleContractorUser: builder.mutation<Entity.ContractorUser[], Entity.CreateContractorUser[]>({
            query: (toInsert) => {
                return ({
                    url: `/${ CONTRACTOR_USER_PATH }/batch`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                for(const item of mutationData) {
                    dispatch(
                            contractorUserApi.util.upsertQueryData('getContractorUser', Entity.PrimaryContractorUser.fromId(item.id), item),
                    );
                }
            },
            invalidatesTags: ['ContractorUsers'],
        }),
        updateContractorUser: builder.mutation<Entity.ContractorUser, Entity.UpdateEntityContractorUser>({
            query: (toUpdate) => {
                return ({
                    url: `/${ CONTRACTOR_USER_PATH }/${toUpdate.id.toPath()}`,
                    method: 'PUT',
                    data: toUpdate.data,
                })
            },
            invalidatesTags: ['ContractorUsers'],
        }),
        updateMultipleContractorUser: builder.mutation<Entity.ContractorUser[], Entity.UpdateEntityContractorUser[]>({
            query: (toUpdate) => {
                return ({
                    url: `/${ CONTRACTOR_USER_PATH }/batch`,
                    method: 'PUT',
                    data: toUpdate,
                })
            },
            invalidatesTags: ['ContractorUsers'],
        }),
        deleteContractorUser: builder.mutation<Entity.ContractorUser, Entity.ContractorUser>({
            query: (toDelete) => {
                return ({
                    url: `/${ CONTRACTOR_USER_PATH }/${Entity.PrimaryContractorUser.fromEntity(toDelete)?.toPath()}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['ContractorUsers'],
        }),
        deleteMultipleContractorUser: builder.mutation<Entity.ContractorUser[], Entity.PrimaryContractorUser[]>({
            query: (ids) => {
                return ({
                    url: `/${ CONTRACTOR_USER_PATH }/batch${Entity.PrimaryContractorUser.toQueryParams(ids)}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['ContractorUsers'],
        }),
    }),
})

export const [contractorUserNormalizer, contractorUserNormalizerMiddleware] = createNormalizationMiddleware(CONTRACTOR_USER_API_REDUCER_KEY, contractorUserApi, {
    devLogging: properties.debugLog,
})

export const {
    useGetContractorUserListQuery,
    useGetContractorUserMultipleQuery,
    useGetContractorUserPresentationQuery,
    useGetContractorUserQuery,
    useInsertContractorUserMutation,
    useInsertMultipleContractorUserMutation,
    useUpdateContractorUserMutation,
    useUpdateMultipleContractorUserMutation,
    useDeleteContractorUserMutation,
    useDeleteMultipleContractorUserMutation,
    
} = contractorUserApi
